import { useEffect, useRef, useState } from "react";
import { ChatCircle, Ear, Microphone, PaperPlaneRight, Robot, SpeakerHigh, SpeakerSlash, Trash, Wrench, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATIC_CHIPS = [
  { label: "Crea una prenotazione", msg: "Voglio creare una nuova prenotazione" },
  { label: "Prenotazioni di oggi", msg: "Mostrami le prenotazioni di oggi" },
  { label: "Come creo un coupon?", msg: "Come creo un coupon?" },
];

// Posizione FAB: mai oltre il 40% dell'altezza viewport; valori corrotti → 0
const clampFabY = (v) => {
  if (!Number.isFinite(v)) return 0;
  const max = Math.round(window.innerHeight * 0.4);
  return Math.min(max, Math.max(0, v));
};

const TOOL_LABELS = {
  cerca_clienti: "Cerco i clienti",
  elenco_prenotazioni: "Leggo le prenotazioni",
  richieste_da_confermare: "Richieste da confermare",
  crea_prenotazione: "Creo la prenotazione",
  cambia_stato_prenotazione: "Aggiorno lo stato",
  elenco_coupon: "Leggo i coupon",
  conta_clienti: "Conto i clienti del segmento",
  crea_coupon: "Creo il coupon",
  crea_campagna: "Preparo la campagna",
  elenco_campagne: "Leggo le campagne",
  elenco_segmenti: "Leggo i segmenti",
  crea_segmento: "Creo il segmento",
  invio_test_campagna: "Invio di prova",
  invia_campagna: "Invio la campagna",
  elenco_workflow: "Leggo i workflow",
  attiva_workflow: "Aggiorno il workflow",
  crea_workflow: "Creo il workflow",
  funnel_coupon: "Analizzo il funnel coupon",
  stats_campagna: "Leggo le statistiche",
  crea_template_whatsapp: "Creo il template WhatsApp",
};

// Lo storico puo' contenere messaggi tecnici del tool calling (role "tool",
// content null o lista multimodale): normalizzare o il render va in crash (pagina bianca)
const sanitizeHistory = (list) => (Array.isArray(list) ? list : [])
  .filter((x) => x && (x.role === "user" || x.role === "assistant"))
  .map((x) => ({ role: x.role, content: typeof x.content === "string" ? x.content : "",
                 tools: Array.isArray(x.tools) ? x.tools : undefined }));

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("assistant_sid") || "");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open && sessionRef.current && messages.length === 0) {
      fetch(`${API}/api/assistant/history?session_id=${sessionRef.current}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dishenda_token")}` },
      }).then((r) => r.json()).then((d) => setMessages(sanitizeHistory(d.messages))).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    window.speechSynthesis?.cancel();
    let fullText = "";
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   Authorization: `Bearer ${localStorage.getItem("dishenda_token")}` },
        body: JSON.stringify({ message: text, session_id: sessionRef.current || null }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const d = JSON.parse(payload);
            if (d.session_id) {
              sessionRef.current = d.session_id;
              localStorage.setItem("assistant_sid", d.session_id);
            }
            if (d.error) {
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], role: "assistant", content: d.error }; return c; });
            }
            if (d.tool) {
              setMessages((m) => {
                const c = [...m];
                const last = { ...c[c.length - 1] };
                const tools = [...(last.tools || [])];
                if (d.tool.status === "running") {
                  tools.push({ name: d.tool.name, status: "running" });
                } else {
                  let idx = -1;
                  for (let i = tools.length - 1; i >= 0; i--) {
                    if (tools[i].name === d.tool.name && tools[i].status === "running") { idx = i; break; }
                  }
                  if (idx >= 0) tools[idx] = { name: d.tool.name, status: "done", ok: d.tool.ok };
                  else tools.push({ name: d.tool.name, status: "done", ok: d.tool.ok });
                }
                last.tools = tools;
                c[c.length - 1] = last;
                return c;
              });
            }
            if (d.delta) {
              fullText += d.delta;
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d.delta };
                return c;
              });
            }
          } catch { /* chunk parziale */ }
        }
      }
      if (ttsOn && fullText.trim()) speak(fullText);
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Errore di connessione, riprova." };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  // FAB riposizionabile: su mobile sta sopra la bottom nav (non copre "Altro");
  // trascinabile verticalmente sul bordo destro; posizione salvata per breakpoint
  // (chiavi localStorage separate mobile/desktop) e riallineata al resize/orientamento
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const storageKey = isMobile ? "assistant_fab_y_m" : "assistant_fab_y_d";
  const [fabY, setFabY] = useState(() => clampFabY(parseInt(localStorage.getItem(storageKey) || "0", 10)));
  const dragRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const raw = parseInt(localStorage.getItem(storageKey) || "0", 10);
    const safe = clampFabY(raw);
    if (safe !== raw) localStorage.setItem(storageKey, String(safe)); // reset valori fuori schermo
    setFabY(safe);
  }, [storageKey]);

  const onFabPointerDown = (e) => {
    dragRef.current = { startY: e.clientY, startOffset: fabY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onFabPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = d.startY - e.clientY; // trascinare in su = offset positivo
    if (Math.abs(dy) > 6) d.moved = true;
    if (d.moved) {
      const max = Math.round(window.innerHeight * 0.4);
      setFabY(Math.min(max, Math.max(0, d.startOffset + dy)));
    }
  };
  const onFabPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.moved) {
      localStorage.setItem(storageKey, String(fabY));
    } else {
      setOpen(!open); // tap semplice = apri/chiudi
    }
  };

  // Suggerimenti: chip intelligenti (contesto reale: richieste in attesa, prenotazioni
  // di oggi) caricati all'apertura; i chip statici restano sempre disponibili
  const [smartChips, setSmartChips] = useState([]);
  useEffect(() => {
    if (!open || messages.length > 0) return;
    const hd = { Authorization: `Bearer ${localStorage.getItem("dishenda_token")}` };
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const chips = [];
    fetch(`${API}/api/reservations/pending-count`, { headers: hd })
      .then((r) => r.json())
      .then((p) => {
        if (p.count > 0) chips.push({ label: `${p.count} richieste da confermare`, msg: "Mostrami le richieste da confermare" });
        return fetch(`${API}/api/reservations?date=${today}`, { headers: hd });
      })
      .then((r) => r.json())
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) chips.push({ label: `Oggi: ${list.length} prenotazioni`, msg: "Mostrami le prenotazioni di oggi" });
        setSmartChips(chips.slice(0, 2));
      })
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Richieste esterne (es. briefing del giorno in Oggi): apre la chat e invia il messaggio
  useEffect(() => {
    const onAsk = (e) => {
      setOpen(true);
      if (e.detail) send(e.detail);
    };
    window.addEventListener("assistant:ask", onAsk);
    return () => window.removeEventListener("assistant:ask", onAsk);
  });

  // Comando vocale via Web Speech API (Chrome/Android; nascosto se non supportato)
  const SpeechRec = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const toggleMic = () => {
    if (!SpeechRec) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SpeechRec();
    rec.lang = "it-IT";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let txt = "";
      for (const res of e.results) txt += res[0].transcript;
      setInput(txt);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  };

  // Hands-free: lettura vocale delle risposte via SpeechSynthesis del browser
  const [ttsOn, setTtsOn] = useState(() => localStorage.getItem("assistant_tts") === "1");
  const speak = (text) => {
    if (!("speechSynthesis" in window)) return;
    const clean = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/[*_#`>]/g, "").replace(/\n{2,}/g, ". ").slice(0, 1200);
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = "it-IT";
    u.rate = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };
  const toggleTts = () => {
    const next = !ttsOn;
    setTtsOn(next);
    localStorage.setItem("assistant_tts", next ? "1" : "0");
    if (!next) window.speechSynthesis?.cancel();
  };

  // Wake word "Ehi Dishenda": ascolto continuo (SOLO con app aperta e schermo
  // acceso — i browser non consentono il microfono in background). Al risveglio
  // apre il pannello e cattura il comando vocale con invio automatico.
  const [wakeOn, setWakeOn] = useState(() => localStorage.getItem("assistant_wake") === "1");
  const [lastHeard, setLastHeard] = useState("");
  const wakeRecRef = useRef(null);
  const wakePausedRef = useRef(false);
  // "Dishenda" non e' una parola di dizionario: il riconoscimento puo' trascriverla
  // in molti modi ("di shenda", "discenda"...) — matching flessibile sulle varianti
  const WAKE_RE = /(dishenda|di[\s-]?shenda|di[\s-]?scenda|discenda|shanda|scienda)/i;

  const stopWake = () => {
    wakePausedRef.current = false;
    wakeRecRef.current?.stop();
    wakeRecRef.current = null;
    setWakeOn(false);
    localStorage.setItem("assistant_wake", "0");
  };

  const listenCommand = () => {
    const rec = new SpeechRec();
    rec.lang = "it-IT";
    rec.interimResults = true;
    rec.continuous = false;
    let finalTxt = "";
    rec.onresult = (e) => {
      let txt = "";
      for (const r of e.results) txt += r[0].transcript;
      setInput(txt);
      if (e.results[e.results.length - 1].isFinal) finalTxt = txt;
    };
    rec.onend = () => {
      setListening(false);
      wakePausedRef.current = false;
      if (wakeRecRef.current) { try { wakeRecRef.current.start(); } catch { /* noop */ } }
      if (finalTxt.trim()) send(finalTxt.trim());
    };
    rec.onerror = () => setListening(false);
    try { rec.start(); } catch { /* noop */ }
    recRef.current = rec;
    setListening(true);
  };

  const startWake = () => {
    if (!SpeechRec) return;
    const rec = new SpeechRec();
    rec.lang = "it-IT";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let heard = "";
      for (const r of e.results) heard += r[0].transcript;
      setLastHeard(heard.trim().slice(-80));
      const res = e.results[e.results.length - 1];
      if (!res.isFinal) return;
      const said = res[0].transcript;
      const m = said.match(WAKE_RE);
      if (!m) return;
      setLastHeard("");
      const cmd = said.slice(m.index + m[0].length).replace(/^[\s,.:]+/, "").trim();
      setOpen(true);
      wakePausedRef.current = true;
      try { rec.stop(); } catch { /* noop */ }
      if (cmd.length > 2) {
        wakePausedRef.current = false;
        if (wakeRecRef.current) { try { wakeRecRef.current.start(); } catch { /* noop */ } }
        send(cmd);
      } else {
        if (localStorage.getItem("assistant_tts") === "1") speak("Dimmi");
        listenCommand();
      }
    };
    rec.onend = () => {
      if (wakeRecRef.current === rec && !wakePausedRef.current) {
        try { rec.start(); } catch { /* noop */ }
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        stopWake();
        toast.error("Microfono non autorizzato: abilitalo nelle impostazioni del browser");
      }
    };
    try { rec.start(); } catch { /* noop */ }
    wakeRecRef.current = rec;
    setWakeOn(true);
    localStorage.setItem("assistant_wake", "1");
  };

  useEffect(() => () => { wakeRecRef.current?.stop(); }, []);

  const fabBottomPx = (isMobile ? 84 : 20) + fabY;
  const fabStyle = {
    bottom: `calc(${fabBottomPx}px + env(safe-area-inset-bottom, 0px))`,
    right: isMobile ? 14 : 20,
    backgroundColor: "#018BFA", width: 52, height: 52,
    touchAction: "none",
  };
  const panelStyle = {
    bottom: `calc(${fabBottomPx + 64}px + env(safe-area-inset-bottom, 0px))`,
    right: isMobile ? 12 : 20,
    height: `min(560px, calc(100dvh - ${fabBottomPx + 84}px))`,
  };

  const newChat = async () => {
    window.speechSynthesis?.cancel();
    if (sessionRef.current) {
      await fetch(`${API}/api/assistant/history?session_id=${sessionRef.current}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("dishenda_token")}` },
      }).catch(() => {});
    }
    sessionRef.current = "";
    localStorage.removeItem("assistant_sid");
    setMessages([]);
    toast.success("Nuova conversazione");
  };

  return (
    <>
      <button onPointerDown={onFabPointerDown} onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp} onPointerCancel={() => { dragRef.current = null; }}
        data-testid="assistant-toggle"
        title="Assistente Dishenda — trascina in su/giù per spostarlo"
        className="fixed z-50 rounded-full text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95 select-none"
        style={fabStyle}>
        {open ? <X size={22} weight="bold" /> : <ChatCircle size={24} weight="fill" />}
        {wakeOn && (
          <span data-testid="assistant-wake-dot"
            className="absolute -top-0.5 -left-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
        )}
      </button>

      {open && (
        <div data-testid="assistant-panel"
          className="fixed z-50 w-[92vw] max-w-sm bg-white rounded-xl border border-zinc-200 shadow-2xl flex flex-col overflow-hidden"
          style={panelStyle}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <div className="flex items-center gap-2">
              <Robot size={18} weight="bold" className="text-sky-600" />
              <div>
                <p className="text-sm font-bold text-zinc-900">Assistente Dishenda</p>
                <p className="text-[10px] text-zinc-400">Ti guido in tutte le funzioni dell'app</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {SpeechRec && (
                <button onClick={() => (wakeOn ? stopWake() : startWake())} data-testid="assistant-wake-toggle"
                  title={wakeOn ? "Disattiva ascolto 'Ehi Dishenda'" : "Attiva ascolto 'Ehi Dishenda' (app aperta)"}
                  className={`p-1.5 rounded-md transition-colors ${
                    wakeOn ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"}`}>
                  <Ear size={15} weight={wakeOn ? "fill" : "bold"} />
                </button>
              )}
              <button onClick={toggleTts} data-testid="assistant-tts-toggle"
                title={ttsOn ? "Disattiva lettura vocale (hands-free)" : "Attiva lettura vocale (hands-free)"}
                className={`p-1.5 rounded-md transition-colors ${
                  ttsOn ? "text-sky-600 bg-sky-50" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"}`}>
                {ttsOn ? <SpeakerHigh size={15} weight="fill" /> : <SpeakerSlash size={15} />}
              </button>
              <button onClick={newChat} data-testid="assistant-new" title="Nuova conversazione"
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors">
                <Trash size={15} />
              </button>
            </div>
          </div>

          {wakeOn && (
            <div data-testid="assistant-wake-status"
              className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 text-[10px] text-emerald-700 flex items-center gap-1.5">
              <Ear size={12} weight="fill" className="animate-pulse shrink-0" />
              <span className="shrink-0">Ascolto attivo: dì "Ehi Dishenda" + il comando</span>
              {lastHeard && (
                <span className="ml-auto truncate italic text-emerald-600/70" data-testid="assistant-wake-heard">
                  ho sentito: "{lastHeard}"
                </span>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-center text-xs text-zinc-400 mt-6 space-y-3" data-testid="assistant-empty">
                <p className="font-semibold text-zinc-500">Come posso aiutarti?</p>
                <div className="flex flex-wrap gap-1.5 justify-center px-1" data-testid="assistant-chips">
                  {smartChips.map((c, i) => (
                    <button key={`s${i}`} onClick={() => send(c.msg)} data-testid={`assistant-chip-smart-${i}`}
                      className="px-2.5 py-1.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors">
                      {c.label}
                    </button>
                  ))}
                  {STATIC_CHIPS.map((c, i) => (
                    <button key={`c${i}`} onClick={() => send(c.msg)} data-testid={`assistant-chip-static-${i}`}
                      className="px-2.5 py-1.5 rounded-full text-[11px] font-medium bg-white text-zinc-600 border border-zinc-200 hover:border-sky-400 hover:text-sky-700 transition-colors">
                      {c.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-300">oppure dettalo col microfono</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-sky-600 text-white rounded-br-sm"
                    : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}
                  data-testid={`assistant-msg-${m.role}`}>
                  {m.tools?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1.5" data-testid="assistant-tools">
                      {m.tools.map((t, ti) => (
                        <span key={ti} data-testid={`assistant-tool-${t.name}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            t.status === "running" ? "bg-sky-100 text-sky-700 animate-pulse"
                            : t.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          <Wrench size={10} weight="bold" />
                          {TOOL_LABELS[t.name] || t.name}{t.status === "running" ? "…" : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {String(m.content ?? "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/^#{1,6}\s*/gm, "")}
                  {m.role === "assistant" && !m.content && busy && (
                    <span className="inline-flex gap-1 py-1">
                      <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                      <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-zinc-100 p-2.5 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={listening ? "Sto ascoltando..." : "Scrivi la tua domanda..."} data-testid="assistant-input"
              className="flex-1 h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-600" />
            {SpeechRec && (
              <button onClick={toggleMic} data-testid="assistant-mic"
                title={listening ? "Interrompi dettatura" : "Detta il messaggio"}
                className={`w-10 h-10 rounded-md flex items-center justify-center transition-colors ${
                  listening ? "bg-red-500 text-white animate-pulse" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"}`}>
                <Microphone size={17} weight={listening ? "fill" : "bold"} />
              </button>
            )}
            <button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send"
              className="w-10 h-10 rounded-md text-white flex items-center justify-center disabled:opacity-40 transition-opacity"
              style={{ backgroundColor: "#018BFA" }}>
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
